/**
 * Purchase request list, and the approval queue.
 *
 * Both are the same screen with a different default filter — the brief's search
 * fields (number, supplier, requester, school, department, status, date, amount) are
 * all one endpoint, so one list serves every role. What differs is what row-level
 * security lets each person see, which the API handles.
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS, REQUEST_STATUS_META, formatMoney } from '@asp/shared';
import type { RequestStatus } from '@asp/shared';
import { api, query } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { formatDate, formatHoursWaiting, formatRelative } from '../lib/format.ts';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui.tsx';

interface RequestRow {
  id: string;
  request_number: string | null;
  status: RequestStatus;
  estimated_total: string;
  priority: string;
  required_delivery_date: string | null;
  submitted_at: string | null;
  created_at: string;
  budget_warning_flag: boolean;
  requires_director_approval: boolean;
  duplicate_of_id: string | null;
  school_name: string;
  school_code: string;
  department_name: string;
  requester_name: string;
  supplier_name: string | null;
  po_number: string | null;
  line_count: number;
}

const STATUS_GROUPS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any status' },
  { value: 'draft,returned_for_amendment', label: 'Needs my attention' },
  { value: 'pending_hod,pending_head_teacher,pending_escalated_approval', label: 'Awaiting approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'po_issued', label: 'Order placed' },
  { value: 'closed', label: 'Closed' },
  { value: 'rejected,cancelled', label: 'Rejected or cancelled' },
];

export function Requests() {
  const [params, setParams] = useSearchParams();
  const { session, can, activeSchoolId } = useSession();

  // Filters live in the URL, so a filtered list can be shared or bookmarked and
  // the back button behaves.
  const status = params.get('status') ?? '';
  const search = params.get('search') ?? '';
  const mine = params.get('mine') ?? '';
  const page = Number(params.get('page') ?? '1');

  const [searchDraft, setSearchDraft] = useState(search);

  const update = (next: Record<string, string | undefined>) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === '') merged.delete(key);
      else merged.set(key, value);
    }
    // Any filter change returns to page one; staying on page 4 of a new result set
    // is the classic way a list looks empty when it is not.
    if (!('page' in next)) merged.delete('page');
    setParams(merged, { replace: true });
  };

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['requests', { status, search, mine, page, activeSchoolId }],
    queryFn: () =>
      api.get<{ items: RequestRow[]; total: number; page: number; pageSize: number }>(
        `/api/requests${query({
          status,
          search,
          mine: mine || undefined,
          page,
          schoolId: activeSchoolId ?? undefined,
        })}`,
      ),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Purchase requests"
        description={
          session && session.accessibleSchools.length > 1 && !activeSchoolId
            ? 'Showing every school you have access to.'
            : undefined
        }
        actions={
          can(PERMISSIONS.REQUEST_DRAFT) && (
            <Link to="/requests/new">
              <Button variant="primary">New request</Button>
            </Link>
          )
        }
      />

      {/* Filters in one row above the list. */}
      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <form
            className="flex min-w-[16rem] flex-1 gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              update({ search: searchDraft });
            }}
          >
            <Input
              type="search"
              placeholder="Request or order number, supplier, person, item…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              aria-label="Search requests"
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>

          <Select
            aria-label="Status"
            value={status}
            onChange={(e) => update({ status: e.target.value })}
            className="w-56"
          >
            {STATUS_GROUPS.map((group) => (
              <option key={group.value} value={group.value}>
                {group.label}
              </option>
            ))}
          </Select>

          <Button
            variant={mine === 'true' ? 'primary' : 'secondary'}
            onClick={() => update({ mine: mine === 'true' ? undefined : 'true' })}
          >
            Only mine
          </Button>

          {(status || search || mine) && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearchDraft('');
                setParams(new URLSearchParams(), { replace: true });
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </Card>

      <Card padded={false}>
        {isPending ? (
          <LoadingState label="Loading requests" />
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : data!.items.length === 0 ? (
          <EmptyState
            title={search || status || mine ? 'Nothing matches those filters' : 'No requests yet'}
            description={
              search || status || mine
                ? 'Try widening the search, or clear the filters.'
                : 'Once someone raises a request it will appear here.'
            }
            action={
              can(PERMISSIONS.REQUEST_DRAFT) && !search && !status && !mine ? (
                <Link to="/requests/new">
                  <Button variant="primary">New request</Button>
                </Link>
              ) : undefined
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Request</Th>
                  <Th>Requested by</Th>
                  <Th>Supplier</Th>
                  <Th numeric>Amount</Th>
                  <Th>Status</Th>
                  <Th>Needed by</Th>
                  <Th>Submitted</Th>
                </tr>
              </thead>
              <tbody>
                {data!.items.map((row) => {
                  const meta = REQUEST_STATUS_META[row.status];
                  return (
                    <Tr key={row.id}>
                      <Td>
                        <Link
                          to={`/requests/${row.id}`}
                          className="font-medium text-navy-700 hover:underline dark:text-navy-200"
                        >
                          {row.request_number ?? 'Draft'}
                        </Link>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1">
                          {row.requires_director_approval && <Badge tone="info">Senior</Badge>}
                          {row.budget_warning_flag && <Badge tone="warning">Over budget</Badge>}
                          {row.duplicate_of_id && <Badge tone="warning">Possible duplicate</Badge>}
                          {row.po_number && <Badge tone="info" dot={false}>{row.po_number}</Badge>}
                          {row.priority === 'urgent' && <Badge tone="danger">Urgent</Badge>}
                        </div>
                      </Td>
                      <Td>
                        <div>{row.requester_name}</div>
                        <div className="text-[12px] text-[var(--ink-muted)]">
                          {row.school_code} · {row.department_name}
                        </div>
                      </Td>
                      <Td>{row.supplier_name ?? '—'}</Td>
                      <Td numeric className="font-semibold">
                        {formatMoney(row.estimated_total)}
                        {row.line_count > 1 && (
                          <div className="text-[12px] font-normal text-[var(--ink-muted)]">
                            {row.line_count} items
                          </div>
                        )}
                      </Td>
                      <Td>
                        <Badge tone={meta?.tone ?? 'neutral'}>{meta?.label ?? row.status}</Badge>
                      </Td>
                      <Td>{formatDate(row.required_delivery_date)}</Td>
                      <Td>
                        <span className="text-[12px] text-[var(--ink-muted)]">
                          {row.submitted_at ? formatRelative(row.submitted_at) : 'not yet'}
                        </span>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-[var(--border-subtle)] px-4 py-3">
                <p className="text-[13px] text-[var(--ink-muted)]">
                  {data!.total} request{data!.total === 1 ? '' : 's'} · page {data!.page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => update({ page: String(page - 1) })}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => update({ page: String(page + 1) })}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Approval queue
//
// The Director dashboard the brief describes: only what needs this person, with
// the amount, the remaining budget and the justification visible before opening it.
// ---------------------------------------------------------------------------

interface PendingRow {
  purchase_request_id: string;
  request_number: string;
  school_name: string;
  department_name: string;
  requester_name: string;
  supplier_name: string | null;
  estimated_total: string;
  hours_waiting: number;
  budget_warning_flag: boolean;
  requires_director_approval: boolean;
  available_budget: string | null;
  current_step_label: string;
  approvals_required: number;
  approvals_received: number;
}

export function Approvals() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['dashboard', 'approvals'],
    queryFn: () => api.get<{ pending: PendingRow[] }>('/api/dashboard/approvals'),
  });

  return (
    <>
      <PageHeader
        title="Awaiting your approval"
        description="Only requests that need a decision from you. Whoever acts first releases the request."
      />

      {isPending ? (
        <Card><LoadingState /></Card>
      ) : error ? (
        <Card><ErrorState error={error} onRetry={refetch} /></Card>
      ) : data!.pending.length === 0 ? (
        <Card>
          <EmptyState
            title="Nothing waiting on you"
            description="When a request reaches your step you will be emailed, and it will appear here."
          />
        </Card>
      ) : (
        <ul className="space-y-3">
          {data!.pending.map((row) => (
            <li key={row.purchase_request_id}>
              <Card padded={false}>
                <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/requests/${row.purchase_request_id}`}
                        className="text-sm font-semibold text-navy-700 hover:underline dark:text-navy-200"
                      >
                        {row.request_number}
                      </Link>
                      {row.requires_director_approval && <Badge tone="info">Senior approval</Badge>}
                      {row.budget_warning_flag && <Badge tone="warning">Exceeds budget</Badge>}
                      {row.approvals_required > 1 && (
                        <Badge tone="pending" dot={false}>
                          {row.approvals_received} of {row.approvals_required} approvals
                        </Badge>
                      )}
                    </div>

                    <p className="mt-1 text-[13px] text-[var(--ink-secondary)]">
                      {row.requester_name} · {row.school_name} · {row.department_name}
                      {row.supplier_name && ` · ${row.supplier_name}`}
                    </p>

                    <p className="mt-1 text-[12px] text-[var(--ink-muted)]">
                      {row.current_step_label} · waiting {formatHoursWaiting(row.hours_waiting)}
                    </p>
                  </div>

                  <div className="flex items-center gap-6 sm:flex-col sm:items-end sm:gap-1">
                    <div className="text-right">
                      <p className="text-lg font-semibold tabular text-[var(--ink-primary)]">
                        {formatMoney(row.estimated_total)}
                      </p>
                      {row.available_budget !== null && (
                        <p
                          className={`text-[12px] tabular ${
                            Number(row.available_budget) < 0
                              ? 'font-semibold text-[var(--color-critical)]'
                              : 'text-[var(--ink-muted)]'
                          }`}
                        >
                          {formatMoney(row.available_budget)} budget left
                        </p>
                      )}
                    </div>
                    <Link to={`/requests/${row.purchase_request_id}`} className="shrink-0">
                      <Button variant="primary" size="lg">
                        Review
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
